import React, { useState } from 'react';
import { TeacherApplicationForm } from '../../model/TeacherApplicationForm';
import { countSelectedAvailability } from '../../utils/teacherApplicationValidators';
import { Province, City } from '../../model/TeacherInfo';

interface SummaryStepProps {
  formData: TeacherApplicationForm;
  setFormData: (data: TeacherApplicationForm) => void;
  setCurrentStep: (step: number) => void;
  errors: { [key: string]: string };
  setErrors: (errors: { [key: string]: string }) => void;
  onSubmit: () => void;
  provinces: Province[];
  cities: City[];
}

const SummaryStep: React.FC<SummaryStepProps> = ({
  formData,
  setCurrentStep,
  errors,
  setErrors,
  onSubmit,
  provinces,
  cities,
}) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const selectedAvailabilityCount = countSelectedAvailability(formData);

  const handleSubmit = () => {
    if (!acceptedTerms) {
      setErrors({ ...errors, terms: 'Devi accettare i termini e condizioni per procedere' });
      return;
    }
    onSubmit();
  };

  return (
    <div className="space-y-6">
      {/* Personal Info Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Dati Personali</h3>
          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            className="text-sm text-fpcred hover:underline font-medium">
            Modifica
          </button>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Nome</dt>
            <dd className="text-base text-gray-900">{formData.first_name || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Cognome</dt>
            <dd className="text-base text-gray-900">{formData.last_name || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Email</dt>
            <dd className="text-base text-gray-900">{formData.email || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Telefono</dt>
            <dd className="text-base text-gray-900">{formData.phone_number || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Provincia</dt>
            <dd className="text-base text-gray-900">
              {provinces.find((p) => p.id === formData.province_id)?.name || '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Città</dt>
            <dd className="text-base text-gray-900">
              {cities.find((c) => c.id === formData.city_id)?.name || '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Indirizzo</dt>
            <dd className="text-base text-gray-900">
              {formData.street_name ? `${formData.street_name} ${formData.street_number}`.trim() : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">CAP</dt>
            <dd className="text-base text-gray-900">{formData.zip || '-'}</dd>
          </div>
        </dl>
      </div>

      {/* Professional Info Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Informazioni Professionali</h3>
          <button
            type="button"
            onClick={() => setCurrentStep(2)}
            className="text-sm text-fpcred hover:underline font-medium">
            Modifica
          </button>
        </div>
        <dl className="space-y-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Auto propria</dt>
            <dd className="text-base text-gray-900">{formData.has_car ? 'Sì' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Qualifiche</dt>
            <dd className="text-base text-gray-900 whitespace-pre-wrap">{formData.qualifications || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Occupazione attuale</dt>
            <dd className="text-base text-gray-900 whitespace-pre-wrap">{formData.current_employment || '-'}</dd>
          </div>
        </dl>
      </div>

      {/* Subjects Section - TODO */}
      <div className="bg-white border border-gray-300 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Materie e Scuole</h3>
          <button
            type="button"
            onClick={() => setCurrentStep(3)}
            className="text-sm text-fpcred hover:underline font-medium">
            Modifica
          </button>
        </div>
        {/* TODO: Format and display subjects */}
        <p className="text-sm text-gray-500">
          {formData.subjects.length > 0 ? `${formData.subjects.length} materia/e selezionata/e` : 'Nessuna materia selezionata'}
        </p>
      </div>

      {/* Availability Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Disponibilità Settimanale</h3>
          <button
            type="button"
            onClick={() => setCurrentStep(4)}
            className="text-sm text-fpcred hover:underline font-medium">
            Modifica
          </button>
        </div>
        <p className="text-base text-gray-900">
          {selectedAvailabilityCount} fasce orarie selezionate su 56
        </p>
        {/* TODO: Show availability grid summary */}
      </div>

      {/* Terms & Conditions */}
      <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms"
            checked={acceptedTerms}
            onChange={(e) => {
              setAcceptedTerms(e.target.checked);
              if (e.target.checked) {
                setErrors({ ...errors, terms: '' });
              }
            }}
            className="mt-1 w-5 h-5 text-fpcred border-gray-300 rounded focus:ring-fpcred"
          />
          <label htmlFor="terms" className="text-sm text-gray-700">
            Dichiaro di aver letto e accettato i{' '}
            <a href="/terms-teacher" target="_blank" className="text-fpcred hover:underline font-medium">
              termini e condizioni
            </a>{' '}
            e autorizzo il trattamento dei miei dati personali secondo la{' '}
            <a href="/privacy" target="_blank" className="text-fpcred hover:underline font-medium">
              privacy policy
            </a>
            .
          </label>
        </div>
        {errors.terms && <p className="mt-2 text-sm text-red-500">{errors.terms}</p>}
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!acceptedTerms}
        className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all ${
          acceptedTerms
            ? 'bg-fpcred text-white hover:bg-red-700 shadow-lg hover:shadow-xl'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}>
        Invia Candidatura
      </button>

      <p className="text-xs text-gray-500 text-center">
        Riceverai una conferma via email all'indirizzo indicato. Ti contatteremo per fissare un colloquio.
      </p>
    </div>
  );
};

export default SummaryStep;
